import { NgIfContext, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    Component,
    DOCUMENT,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    TemplateRef,
    inject,
} from '@angular/core';

import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import { PopupsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { OptionalNonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import {
    HORIZONTAL_GESTURE_CONFIG,
    ModalDismissReasons,
    ModalGestureAnimationService,
    ModalGestureHorizontalConfig,
    ModalGestureIsClosingService,
    ModalGestureVerticalConfig,
    ModalHorizontalGestureService,
    ModalOptions,
    ModalVerticalGestureService,
    VERTICAL_GESTURE_CONFIG,
    popupManualClose,
} from '@frontend/sports/modal/feature';
import { MODAL_WINDOW_CONFIG } from '@frontend/sports/modal/feature/provider';
import { Store } from '@ngrx/store';

export enum PopupAction {
    Open = 'open',
    ManualClose = 'manualClose',
    CloseByNavigation = 'closeByNavigation',
    CloseByMybets = 'CloseByMybets',
    CloseByNextPopup = 'CloseByNextPopup',
    CloseByPan = 'CloseByPan',
    SilentClose = 'SilentClose',
}

export interface PopupClosingResult {
    closeAction: PopupAction;
}

export enum CloseButtonType {
    Icon = 'Icon',
    Button = 'Button',
}

export interface GestureOptions {
    enabled?: boolean;
    headerSelector?: string;
    scrollTargetQuerySelector?: string;
    restorePreviousUrl?: boolean;
}

@Component({
    selector: 'ms-modal-window',
    templateUrl: './modal-window.component.html',
    imports: [AutomationDirective, NgTemplateOutlet],
    providers: [
        ModalVerticalGestureService,
        ModalHorizontalGestureService,
        {
            provide: VERTICAL_GESTURE_CONFIG,
            useClass: ModalGestureVerticalConfig,
        },
        {
            provide: HORIZONTAL_GESTURE_CONFIG,
            useClass: ModalGestureHorizontalConfig,
        },
        ModalGestureIsClosingService,
    ],
})
export class ModalWindowComponent implements OnInit, AfterViewInit, OnDestroy {
    @HostBinding('attr.aria-labelledby')
    @Input()
    ariaLabelledBy: string;

    /**
     * Provide a template to be rendered inside the modal-header.
     *
     * @type {TemplateRef<any>}
     * @memberof ModalOptions
     */
    @Input()
    headerTemplate: TemplateRef<NgIfContext>;

    slideOutFromTopToButtom = 'modal-slide-out-to-bottom';
    slideOutToRight = 'modal-slide-out-to-right';

    @Input() backdrop: boolean | string = true;
    @Input() centered: string;
    @Input() keyboard = true;
    @Input() size: string;
    @Input() windowClass: string;
    @Input() showDialogHeader?: boolean;
    @Input() title?: string;
    @Input() hideCloseButton?: boolean;
    @Input() showBackNav?: boolean;
    @Input() animatePopupDismiss = false;
    @Input() showCloseIcon = true;
    @Input() closePrevious?: boolean;
    @Input() gestureOptions: GestureOptions;

    @Output() dismiss = new EventEmitter<{ closeAction: PopupAction.ManualClose }>();

    @HostBinding('attr.role') role = 'dialog';
    @HostBinding('attr.tabindex') tabindex = '-1';

    @HostBinding()
    get class(): string {
        return 'modal ' + (this.windowClass ? ' ' + this.windowClass : '');
    }

    showCloseBtnText?: boolean;
    closeText: string = '';

    private readonly _doc = inject(DOCUMENT);

    private readonly siteCore = inject(Sitecore);
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly timerService = inject(TimerService);
    private readonly store$ = inject(Store);
    private readonly modalVerticalGestureService = inject(ModalVerticalGestureService);
    private readonly modalGestureAnimationService = inject(ModalGestureAnimationService);
    private readonly modalHorizontalGestureService = inject(ModalHorizontalGestureService);
    private readonly config = inject<Partial<ModalOptions> | undefined>(OptionalNonRootToken(MODAL_WINDOW_CONFIG));
    private readonly popUpsConfig = inject(PopupsConfig);

    constructor() {
        if (this.config) {
            this.animatePopupDismiss = this.config.animatePopupDismiss ?? false;
            this.showCloseIcon = this.popUpsConfig.closeButtonType === CloseButtonType.Icon || (this.config.showCloseIcon ?? true);
        }
        this.closeText = this.siteCore.globalMessages.Close;
    }

    close(): void {
        if (this.gestureOptions.enabled) {
            this.modalGestureAnimationService.slideOutToBottom(this.elementRef.nativeElement).finally(() => {
                this.dismiss.emit({ closeAction: PopupAction.ManualClose });
            });
        } else {
            this.renderer.addClass(this.elementRef.nativeElement, this.slideOutFromTopToButtom);
            this.dismissPopup();
        }

        this.store$.dispatch(popupManualClose());
    }

    goBack(): void {
        this.renderer.addClass(this.elementRef.nativeElement, this.slideOutToRight);
        this.dismissPopup();
    }

    private dismissPopup() {
        if (this.animatePopupDismiss) {
            this.timerService.setTimeoutOutsideAngular(() => {
                this.dismiss.emit({ closeAction: PopupAction.ManualClose });
            }, 500);
        } else {
            this.dismiss.emit({ closeAction: PopupAction.ManualClose });
        }
    }

    @HostListener('mousedown', ['$event'])
    @HostListener('touch', ['$event'])
    backdropClick(event: Event): void {
        if (this.backdrop === true && this.elementRef.nativeElement === event.target) {
            this.dismissWindow(ModalDismissReasons.BackdropClick);
        }
    }

    //Prevent any scroll event within modal container to bubble up to body (preventing unwanted double scroll appearing)
    @HostListener('scroll', ['$event'])
    @HostListener('touchmove', ['$event'])
    preventScrolPropogation(event: Event) {
        event.stopPropagation();
    }

    @HostListener('keyup.esc', ['$event'])
    escKey(event: Event): void {
        if (this.keyboard && !event.defaultPrevented) {
            this.dismissWindow(ModalDismissReasons.Esc);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private dismissWindow(reason: any): void {
        if (this.animatePopupDismiss) {
            this.renderer.addClass(this.elementRef.nativeElement, this.slideOutFromTopToButtom);
            this.timerService.setTimeoutOutsideAngular(() => {
                this.dismiss.emit(reason);
            }, 500);
        } else {
            this.dismiss.emit(reason);
        }
    }

    ngOnInit(): void {
        this.renderer.addClass(this._doc.body, 'modal-open');
    }

    ngAfterViewInit(): void {
        this.timerService.setTimeoutOutsideAngular(() => {
            if (!this.elementRef.nativeElement.contains(this._doc.activeElement)) {
                this.elementRef.nativeElement['focus'].apply(this.elementRef.nativeElement, []);
            }
        });

        if (this.gestureOptions.headerSelector === 'event-details-modal') {
            this.modalHorizontalGestureService.addGestures(this.elementRef.nativeElement, this.gestureOptions);
        } else {
            this.modalVerticalGestureService.addGestures(this.elementRef.nativeElement, this.gestureOptions);
        }
    }

    ngOnDestroy(): void {
        this.renderer.removeClass(this._doc.body, 'modal-open');
    }
}
